import {
  Col,
  Flex,
  Form,
  Input,
  Paragraph,
  Row,
  Switch,
  Textarea,
  type FormInstance,
} from "@ir/ui";
import { ErrorBoundary } from "@sentry/react";
import { ImageCrop } from "@src/presentation/components/common/imagecrop";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BRAND_DISPLAY_NAME_MAX_LENGTH,
  BRAND_KEY_MAX_LENGTH,
  BRAND_NAME_MAX_LENGTH,
} from "@src/domain/dto/brands/UpdateBrand.dto";
import {
  createDuplicateKeyValidator,
  DESCRIPTION_MAX_LENGTH_RULES,
  generateEntityKey,
  getInputValue,
  isAddEditFormDirty,
  LOGO_ACCEPT,
  LOGO_FILE_VALIDATION_RULES,
  sanitizeKeyInput,
} from "./addEditFormUtils";
import { BRAND_KEY_ALREADY_EXISTS_MESSAGE } from "./brandKeyValidation";
import { SavedLogoPreview } from "./SavedLogoPreview";

const BRAND_KEY_CHARS = /[^A-Z0-9-]/g;

function toDirtyComparable(values: BrandFormValues) {
  return {
    name: values.brandName,
    displayName: values.brandDisplayName,
    description: values.brandDescription,
    isActive: values.brandIsActive,
    logoFile: values.brandLogoFile,
    removeLogo: values.removeBrandLogo,
  };
}

export interface BrandFormValues {
  brandId?: number | null;
  organizationId: number;
  organizationKey: string;
  brandKey: string;
  brandName: string;
  brandDisplayName: string;
  brandDescription?: string | null;
  brandLogoFile?: File | null;
  removeBrandLogo?: boolean;
  brandIsActive: boolean;
}

interface BrandsAddEditFormProps {
  form: FormInstance<BrandFormValues>;
  mode: "create" | "edit";
  initialValues: BrandFormValues;
  existingLogoUrl?: string | null;
  existingBrandKeys?: ReadonlySet<string>;
  excludeBrandKey?: string;
  onSubmit: (values: BrandFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

export const BrandsAddEditForm = ({
  form,
  mode,
  initialValues,
  existingLogoUrl,
  existingBrandKeys,
  excludeBrandKey,
  onSubmit,
  onFormValidityChange,
}: BrandsAddEditFormProps) => {
  const [isKeyEditedManually, setIsKeyEditedManually] = useState(false);
  const [showSavedLogoPreview, setShowSavedLogoPreview] = useState(true);

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);
    const values = form.getFieldsValue();
    const hasRequiredValues =
      (values.brandName?.trim() ?? "") !== "" &&
      (values.brandDisplayName?.trim() ?? "") !== "" &&
      (values.brandKey?.trim() ?? "") !== "";
    const isDirty =
      mode === "create" ||
      isAddEditFormDirty(
        toDirtyComparable(values),
        toDirtyComparable(initialValues),
      );
    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    setIsKeyEditedManually(
      mode === "edit" ? true : initialValues.brandKey !== "",
    );
    setShowSavedLogoPreview(true);
    updateFormValidity();
  }, [form, initialValues, mode, updateFormValidity]);

  const brandKeyDisabled = useMemo(() => mode === "edit", [mode]);

  const brandNameRules = useMemo(
    () => [
      { required: true, message: "Brand name is required" },
      {
        max: BRAND_NAME_MAX_LENGTH,
        message: `Brand name must be less than or equal to ${BRAND_NAME_MAX_LENGTH} characters.`,
      },
    ],
    [],
  );

  const brandDisplayNameRules = useMemo(
    () => [
      { required: true, message: "Display name is required" },
      {
        max: BRAND_DISPLAY_NAME_MAX_LENGTH,
        message: `Display name must be less than or equal to ${BRAND_DISPLAY_NAME_MAX_LENGTH} characters.`,
      },
    ],
    [],
  );

  const brandKeyRules = useMemo(
    () => [
      { required: true, message: "Brand key is required" },
      {
        pattern: /^[A-Z0-9]+(-[A-Z0-9]+)*$/,
        message: "Only uppercase letters and hyphens are allowed",
      },
      {
        max: BRAND_KEY_MAX_LENGTH,
        message: `Brand key must be less than or equal to ${BRAND_KEY_MAX_LENGTH} characters.`,
      },
      {
        validator: createDuplicateKeyValidator(
          existingBrandKeys,
          excludeBrandKey,
          BRAND_KEY_ALREADY_EXISTS_MESSAGE,
        ),
      },
    ],
    [existingBrandKeys, excludeBrandKey],
  );

  useEffect(() => {
    const key = form.getFieldValue("brandKey");
    if (!key?.trim()) return;
    form.validateFields(["brandKey"]).catch(() => {});
  }, [existingBrandKeys, excludeBrandKey, form]);

  const syncBrandKeyFromBrandName = (valueOrEvent: unknown) => {
    if (brandKeyDisabled || isKeyEditedManually) return;
    form.setFieldsValue({
      brandKey: generateEntityKey(
        getInputValue(valueOrEvent),
        BRAND_KEY_CHARS,
        BRAND_KEY_MAX_LENGTH,
      ),
    });
    form.validateFields(["brandKey"]).catch(() => {});
  };

  const handleBrandNameChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      brandName: getInputValue(valueOrEvent),
    });
    syncBrandKeyFromBrandName(valueOrEvent);
    form.validateFields(["brandName"]).catch(() => {});
  };

  const handleBrandDisplayNameChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      brandDisplayName: getInputValue(valueOrEvent),
    });
    form.validateFields(["brandDisplayName"]).catch(() => {});
  };

  const handleValuesChange = (changed: Partial<BrandFormValues>) => {
    if (changed.removeBrandLogo === true) {
      form.setFieldsValue({ brandLogoFile: null });
    }
    if (changed.brandLogoFile) {
      form.setFieldsValue({ removeBrandLogo: false });
      setShowSavedLogoPreview(false);
    }
    updateFormValidity();
  };

  const trimmedExistingLogoUrl = existingLogoUrl?.trim() ?? "";

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        onValuesChange={handleValuesChange}
        className="organizations-add-edit"
      >
        <Form.Item name="organizationId" className="hidden">
          <Input aria-hidden />
        </Form.Item>
        <Form.Item name="organizationKey" className="hidden">
          <Input aria-hidden />
        </Form.Item>

        {mode === "edit" && (
          <Form.Item
            name="removeBrandLogo"
            valuePropName="isSelected"
            initialValue={false}
            className="hidden"
          >
            <Switch size="small" aria-hidden />
          </Form.Item>
        )}

        <Row>
          <Col span={6}>
            <Form.Item
              name="brandName"
              label="Brand Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={brandNameRules}
            >
              <Input
                placeholder="Brand name"
                onChange={handleBrandNameChange}
                onInput={handleBrandNameChange}
                onKeyUp={handleBrandNameChange}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="brandDisplayName"
              label="Display Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={brandDisplayNameRules}
            >
              <Input
                placeholder="Brand display name"
                onChange={handleBrandDisplayNameChange}
                onInput={handleBrandDisplayNameChange}
                onKeyUp={handleBrandDisplayNameChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <Form.Item
              name="brandKey"
              label="Brand Key"
              validateTrigger={["onChange", "onBlur"]}
              rules={brandKeyRules}
              helpText="Once brand key is created, it cannot be changed."
            >
              <Input
                placeholder="Brand key"
                isDisabled={brandKeyDisabled}
                onChange={(valueOrEvent) => {
                  if (brandKeyDisabled) return;
                  setIsKeyEditedManually(true);
                  form.setFieldsValue({
                    brandKey: sanitizeKeyInput(
                      getInputValue(valueOrEvent),
                      BRAND_KEY_CHARS,
                    ),
                  });
                  form.validateFields(["brandKey"]).catch(() => {});
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item>
              <Flex
                align="start"
                gap={1}
                className="status-switch"
                direction="column"
              >
                <Paragraph className="m-0 custom-label">Brand Status</Paragraph>
                <Form.Item
                  name="brandIsActive"
                  valuePropName="isSelected"
                  noStyle
                >
                  <Switch
                    className="p-0"
                    size="small"
                    aria-label="Brand active status"
                  />
                </Form.Item>
              </Flex>
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={9}>
            <Form.Item
              name="brandDescription"
              label="Brand Description"
              validateTrigger={["onChange", "onBlur"]}
              rules={DESCRIPTION_MAX_LENGTH_RULES}
            >
              <Textarea placeholder="Brand description" />
            </Form.Item>
          </Col>
          <Col span={3}>
            {mode === "edit" && trimmedExistingLogoUrl !== "" && (
              <SavedLogoPreview
                logoUrl={trimmedExistingLogoUrl}
                showPreview={showSavedLogoPreview}
                onRemove={() => {
                  form.setFieldsValue({
                    removeBrandLogo: true,
                    brandLogoFile: null,
                  });
                  setShowSavedLogoPreview(false);
                  updateFormValidity();
                }}
              />
            )}
          </Col>
        </Row>

        <Form.Item
          name="brandLogoFile"
          label="Brand Logo"
          validateTrigger={["onChange", "onBlur"]}
          rules={LOGO_FILE_VALIDATION_RULES}
        >
          <ImageCrop
            accept={LOGO_ACCEPT}
            aspect={1}
            outputSize={200}
            outputFileName="brand-logo.jpg"
            onApply={() => {
              form.validateFields(["brandLogoFile"]).catch(() => {});
            }}
          />
        </Form.Item>
      </Form>
    </ErrorBoundary>
  );
};
